import React, { useCallback, useEffect, useState } from "react";
import DocumentUploadModal, {
  type DocumentUploadSaveResult,
} from "@src/packages/document-vault/DocumentUploadModal";
import { useTranslations } from "@src/packages/i18n";
import {
  hasVisualEvidenceUploadedFiles,
  useFertilityTesting,
} from "./FertilityTestingContext";

const visualEvidenceUploadedFile = {
  id: "visual-evidence-fertility-testing-upload",
  name: "fertility-testing-report.pdf",
};

/**
 * Reuses the document-vault OCR flow from fertility-testing questions.
 * The saved result is refreshed into the flow so upload and manual answers
 * share the same state before the patient continues.
 */
export function useFertilityTestingUpload() {
  const t = useTranslations();
  const { state, updateState, refreshFromPatient } = useFertilityTesting();
  const [visible, setVisible] = useState(false);
  const visualEvidenceRoute =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("dev_visual_evidence") ===
      "true";
  const visualEvidenceUploadedFilesEnabled =
    process.env.EXPO_PUBLIC_VISUAL_EVIDENCE_UPLOADED_FILES === "true" ||
    hasVisualEvidenceUploadedFiles() ||
    (visualEvidenceRoute &&
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("dev_uploaded_files") ===
        "true");
  const visualEvidenceUploadedFiles = visualEvidenceUploadedFilesEnabled
    ? [visualEvidenceUploadedFile]
    : state.uploadedFiles;

  useEffect(() => {
    if (
      !visualEvidenceUploadedFilesEnabled ||
      state.uploadedFiles.some(
        (file) => file.id === visualEvidenceUploadedFile.id,
      )
    ) {
      return;
    }

    updateState({ uploadedFiles: [visualEvidenceUploadedFile] });
  }, [state.uploadedFiles, updateState, visualEvidenceUploadedFilesEnabled]);

  const open = useCallback(() => setVisible(true), []);
  const close = useCallback(() => setVisible(false), []);
  const handleSaved = useCallback(
    (result: DocumentUploadSaveResult) => {
      if (result.fileName) {
        const uploadedFile = {
          id: `medical-history-upload-${Date.now()}`,
          name: result.fileName,
        };
        updateState({
          uploadedFiles: [...state.uploadedFiles, uploadedFile],
        });
      }
      close();
      void refreshFromPatient();
    },
    [close, refreshFromPatient, state.uploadedFiles, updateState],
  );

  return {
    uploadAction: {
      label: t("med_history.fertility.testing.upload_label"),
      accessibilityLabel: t("med_history.fertility.testing.upload_label"),
      onPress: open,
    },
    uploadModal: (
      <DocumentUploadModal
        visible={visible}
        onClose={close}
        onSave={handleSaved}
        defaultCategory="medical_records"
        defaultTags={[
          "medical_records",
          "medical-history",
          "fertility-testing",
        ]}
        autoPickOnOpen={!visualEvidenceRoute}
      />
    ),
    uploadedFiles: visualEvidenceUploadedFiles,
  };
}
